import { FilterType } from '@bestseller-bit/frontend-community.bootstrapping.application-shell';
import { Autocomplete, Grid, TextField } from '@mui/material';
import { uniqBy } from 'lodash';
import { FC, useCallback, useMemo } from 'react';
import { useFilterContext } from 'src/contexts/FilterContext';
import { Filter, FilterOption } from 'src/domain';
import { useFilterStoreOptions } from 'src/hooks/useFilterStoreOptions';

export const StoreFilters: FC<{
    addFilter: (addition: Array<Omit<FilterType, 'name'>> | Omit<FilterType, 'name'>) => void;
    removeFilter?: (value: string | string[], filterGroup?: string) => void;
    onlyOneValuePrFilter?: boolean;
    filters?: FilterType[];
    addFilterCallback: (addFilterCallback: () => void) => void;
}> = ({ addFilter, removeFilter }) => {
    const [filterContext, setFilterContext] = useFilterContext();
    const { filterOptions } = useFilterStoreOptions();

    const { chainOptions, countryOptions, clusterOptions, partnerOptions } = useMemo(
        () => ({
            chainOptions: uniqBy(filterOptions.chains, (chain) => chain.label).sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
            countryOptions: uniqBy(filterOptions.countries, (country) => country.label).sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
            clusterOptions: uniqBy(filterOptions.clusters, (cluster) => cluster.label).sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
            partnerOptions: uniqBy(filterOptions.partners, (partner) => partner.label).sort((a, b) =>
                a.label.localeCompare(b.label)
            ),
        }),
        [filterOptions]
    );

    const isOptionEqualToValue = useCallback((option: FilterOption, value: FilterOption) => {
        return option?.value === value?.value;
    }, []);

    const handleChainChange = (selectedChains: FilterOption[]) => {
        removeFilter?.('chains', 'chains');

        if (selectedChains.length > 0) {
            addFilter(
                selectedChains.map((chain) => ({
                    key: 'chains',
                    value: chain.value?.toString() || '',
                    displayName: chain.label,
                    filterGroup: 'chains',
                }))
            );
        }

        setFilterContext((prev: Filter) => ({
            ...prev,
            chains: selectedChains,
        }));
    };

    const handleCountryChange = (selectedCountries: FilterOption[]) => {
        removeFilter?.('countries', 'countries');

        if (selectedCountries.length > 0) {
            addFilter(
                selectedCountries.map((country) => ({
                    key: 'countries',
                    value: country.value?.toString() || '',
                    displayName: country.label,
                    filterGroup: 'countries',
                }))
            );
        }

        setFilterContext((prev: Filter) => ({
            ...prev,
            countries: selectedCountries,
        }));
    };

    const handleClusterChange = (selectedClusters: FilterOption[]) => {
        removeFilter?.('clusters', 'clusters');

        if (selectedClusters.length > 0) {
            addFilter(
                selectedClusters.map((cluster) => ({
                    key: 'clusters',
                    value: cluster.value?.toString() || '',
                    displayName: cluster.label,
                    filterGroup: 'clusters',
                }))
            );
        }

        setFilterContext((prev: Filter) => ({
            ...prev,
            clusters: selectedClusters,
        }));
    };

    const handlePartnerChange = (selectedPartners: FilterOption[]) => {
        removeFilter?.('partners', 'partners');

        if (selectedPartners.length > 0) {
            addFilter(
                selectedPartners.map((partner) => ({
                    key: 'partners',
                    value: partner.value?.toString() || '',
                    displayName: partner.label,
                    filterGroup: 'partners',
                }))
            );
        }

        setFilterContext((prev: Filter) => ({
            ...prev,
            partners: selectedPartners,
        }));
    };

    return (
        <Grid
            container
            spacing={3}
            sx={{
                alignItems: 'stretch',
            }}
        >
            <Grid size={12}>
                <Autocomplete
                    limitTags={1}
                    multiple
                    filterSelectedOptions
                    options={chainOptions as FilterOption[]}
                    value={filterContext.chains}
                    isOptionEqualToValue={isOptionEqualToValue}
                    onChange={(_event, value) => handleChainChange(value)}
                    renderInput={(params) => <TextField {...params} label="Chain" />}
                />
            </Grid>
            <Grid size={12}>
                <Autocomplete
                    limitTags={1}
                    multiple
                    filterSelectedOptions
                    options={countryOptions as FilterOption[]}
                    value={filterContext.countries}
                    isOptionEqualToValue={isOptionEqualToValue}
                    onChange={(_event, value) => handleCountryChange(value)}
                    renderInput={(params) => <TextField {...params} label="Country" />}
                />
            </Grid>
            <Grid size={12}>
                <Autocomplete
                    limitTags={1}
                    multiple
                    filterSelectedOptions
                    options={clusterOptions as FilterOption[]}
                    value={filterContext.clusters}
                    isOptionEqualToValue={isOptionEqualToValue}
                    onChange={(_event, value) => handleClusterChange(value)}
                    renderInput={(params) => <TextField {...params} label="Cluster" />}
                />
            </Grid>
            <Grid size={12}>
                <Autocomplete
                    limitTags={1}
                    multiple
                    filterSelectedOptions
                    options={partnerOptions as FilterOption[]}
                    value={filterContext.partners}
                    isOptionEqualToValue={isOptionEqualToValue}
                    onChange={(_event, value) => handlePartnerChange(value)}
                    renderInput={(params) => <TextField {...params} label="Partner" />}
                />
            </Grid>
        </Grid>
    );
};
